import type { NodeSpec } from '../../types/nodeSpec';

/**
 * Dampens how fast turbulence tracks wall time × Time Speed. The hash-style noise
 * is sensitive to time, so this keeps the low end of the Time Speed slider usable; pair
 * with a wider `turbulenceTimeSpeed` max for the same "grungy" top end as before.
 */
export const TURBULENCE_TIME_INTRINSIC_SCALE = 0.1;

export const turbulenceNodeSpec: NodeSpec = {
  id: 'turbulence',
  category: 'Distort',
  displayName: 'Turbulence',
  icon: 'dots-nine',
  description: 'Applies multi-stage coordinate warping for complex, organic distortions',
  inputs: [
    {
      name: 'in',
      type: 'vec2',
      label: 'UV'
    }
  ],
  outputs: [
    {
      name: 'out',
      type: 'vec2',
      label: 'Displaced UV'
    }
  ],
  parameters: {
    turbulenceScale: {
      type: 'float',
      default: 1.0,
      min: 0.1,
      max: 10.0,
      step: 0.01,
      label: 'Scale'
    },
    turbulenceStrength: {
      type: 'float',
      default: 0.5,
      min: 0.0,
      max: 2.0,
      step: 0.001,
      label: 'Strength'
    },
    turbulenceIterations: {
      type: 'int',
      default: 3,
      min: 1,
      max: 8,
      step: 1,
      label: 'Iterations'
    },
    turbulenceTimeSpeed: {
      type: 'float',
      default: 1.0,
      min: 0.0,
      max: 50.0,
      step: 0.001,
      label: 'Time Speed'
    },
    turbulenceTimeOffset: {
      type: 'float',
      default: 0.0,
      min: -100.0,
      max: 100.0,
      step: 0.001,
      label: 'Time Offset',
      knobPolarity: 'two-sided' }
  },
  parameterGroups: [
    {
      id: 'turbulence-main',
      label: 'Turbulence',
      parameters: ['turbulenceScale', 'turbulenceStrength', 'turbulenceIterations'],
      collapsible: true,
      defaultCollapsed: false
    },
    {
      id: 'turbulence-animation',
      label: 'Animation',
      parameters: ['turbulenceTimeSpeed', 'turbulenceTimeOffset'],
      collapsible: true,
      defaultCollapsed: true
    }
  ],
  parameterLayout: {
    elements: [
      {
        type: 'grid',
        parameters: ['turbulenceScale', 'turbulenceStrength', 'turbulenceIterations'],
        layout: { columns: 2, parameterSpan: { turbulenceIterations: 2 } }
      },
      {
        type: 'grid',
        label: 'Animation',
        parameters: ['turbulenceTimeSpeed', 'turbulenceTimeOffset'],
        layout: { columns: 2 }
      }
    ]
  },
  functions: `
// Simple noise for turbulence
vec2 noise2D(vec2 p) {
  return vec2(
    fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453),
    fract(sin(dot(p, vec2(12.9898, 78.233) + vec2(1.0))) * 43758.5453)
  );
}

// Domain warping (strength passed from main so param connections are evaluated in correct execution order)
vec2 turbulence(vec2 p, float time, int iterations, float strength) {
  vec2 q = p;
  int iterCount = max(iterations, 1);
  // Strength is specified in "UV-ish" units in the UI. Without scaling, values >~0.2 quickly
  // jump multiple UV tiles and can appear to "saturate" (many downstream nodes wrap/periodic).
  float s = clamp(strength, 0.0, 2.0) * 0.2;
  if (isnan(s) || isinf(s)) s = 0.0; // guard: avoid NaN/Inf propagating to single-color output
  
  // Use a 2D time vector so animation isn't just diagonal drift.
  vec2 t = vec2(time, time * 1.37);

  for (int i = 0; i < 8; i++) {
    if (i >= iterCount) break;
    
    float scale = pow(2.0, float(i));
    float safeScale = max(scale, 0.001);
    vec2 offset = noise2D(q * safeScale + t) * 2.0 - 1.0;
    q += offset * s / safeScale;
  }
  
  return q;
}
`,
  mainCode: `
  float turbulenceTime = ($time + $param.turbulenceTimeOffset) * $param.turbulenceTimeSpeed * ${TURBULENCE_TIME_INTRINSIC_SCALE};
  float turbulenceScale = max($param.turbulenceScale, 0.001);
  // Strength should stay meaningful regardless of scale.
  // We warp in scaled space then divide back, so multiply by scale to keep output-space amplitude stable.
  float turbulenceStrength = clamp($param.turbulenceStrength, 0.0, 2.0) * turbulenceScale;
  // Keep output in the same coordinate space as input:
  // scale controls noise frequency, not a permanent UV zoom.
  $output.out =
    turbulence($input.in * turbulenceScale, turbulenceTime, $param.turbulenceIterations, turbulenceStrength) /
    turbulenceScale;
`
};
